/**
 * 把一份服务端产出的完整 HTML 文档交给浏览器打印。
 *
 * 用于「打印 / 导出 PDF」这类**文档由服务端渲染**的出口（见 GET /orders/export/print）：
 * 前端取回 HTML 文本 → 塞进常驻的同源 iframe → 调它的 print()。
 * 目标打印机选「另存为 PDF」即得到 PDF，文字可选中可检索，分页由浏览器引擎负责。
 *
 * iframe 是复用的单个节点而不是每次建再拆：Safari 的打印对话框是异步的，
 * 打印途中把 iframe 移出文档会得到一张空白页。
 */
let printFrame: HTMLIFrameElement | null = null;

function ensureFrame(): HTMLIFrameElement {
  if (printFrame) return printFrame;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  // 尺寸压到 0 但不能 display:none —— 隐藏的 iframe 在 Firefox 上打印出来是空白。
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  printFrame = frame;
  return frame;
}

/** 文档加载超时：超过这个时间还没 onload 基本就是被拦截或内容异常，不能让调用方永远悬着。 */
const LOAD_TIMEOUT_MS = 15000;

export async function printHtmlDocument(html: string): Promise<void> {
  const frame = ensureFrame();
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('打印文档加载超时，请重试')), LOAD_TIMEOUT_MS);
      frame.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      frame.src = url;
    });
    const win = frame.contentWindow;
    if (!win) throw new Error('打印窗口无法打开，请检查浏览器是否拦截了打印');
    win.focus(); // 不 focus 时部分浏览器会把打印请求发给当前页面而不是文档
    win.print();
  } finally {
    // 只释放这一次文档的 URL 引用：文档此时已在 iframe 中加载完成，回收引用不会打断打印。
    URL.revokeObjectURL(url);
  }
}
